import { lazy, Suspense, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useCardVersion, useRestoreVersion } from '@/entities/history';
import { FormError } from '@/shared/forms';
import { contrastText } from '@/shared/lib/color';
import { formatCalendarDate, formatClock } from '@/shared/lib/datetime';
import { Button, ConfirmDialog, ErrorState, LoadingState, useToast } from '@/shared/ui';

import styles from './CardHistory.module.css';

const Markdown = lazy(() =>
  import('@/shared/ui/Markdown').then((module) => ({ default: module.Markdown })),
);

interface Props {
  cardId: string;
  boardId: string;
  versionId: string;
  cardVersion: number;
  onBack: () => void;
  onRestored: () => void;
}

/** Read-only view of a saved state, with a confirmed "Restore this version". */
export function VersionPreview({
  cardId,
  boardId,
  versionId,
  cardVersion,
  onBack,
  onRestored,
}: Props) {
  const { t, i18n } = useTranslation();
  const version = useCardVersion(cardId, versionId);
  const restore = useRestoreVersion(cardId, boardId);
  const toast = useToast();
  const [confirming, setConfirming] = useState(false);

  if (version.isPending) return <LoadingState />;
  if (version.isError) {
    return (
      <div className={styles.section}>
        <ErrorState error={version.error} onRetry={() => void version.refetch()} />
        <Button onClick={onBack}>{t('history.back')}</Button>
      </div>
    );
  }
  const { snapshot, version_number: number } = version.data;
  const deadline = snapshot.deadline;
  const clock = (time: string | null) => (time ? ` ${formatClock(time)}` : '');
  const deadlineText = deadline
    ? `${deadline.start_date ? `${formatCalendarDate(deadline.start_date, i18n.language, true)}${clock(deadline.start_time)} → ` : ''}${formatCalendarDate(deadline.date, i18n.language, true)}${clock(deadline.time)}`
    : t('history.noDeadline');

  return (
    <section className={styles.section} aria-label={t('history.previewTitle', { number })}>
      <div className={styles.previewHead}>
        <Button size="sm" onClick={onBack}>
          ← {t('history.back')}
        </Button>
        <h3 className={styles.previewTitle}>{t('history.previewTitle', { number })}</h3>
      </div>
      <div className={styles.preview} data-testid="version-preview">
        <p className={styles.previewCardTitle}>{snapshot.title}</p>
        <p className={styles.meta}>
          {snapshot.completed_at ? t('history.completedYes') : t('history.completedNo')}
          {' · '}
          {deadlineText}
        </p>
        <div className={styles.labels}>
          {snapshot.labels.length === 0 && (
            <span className={styles.meta}>{t('history.noLabels')}</span>
          )}
          {snapshot.labels.map((label) => (
            <span
              key={label.label_id}
              className={`${styles.label} ${label.exists ? '' : styles.labelGone}`}
              style={{ background: label.color, color: contrastText(label.color) }}
              title={label.exists ? undefined : t('history.labelGone')}
            >
              {label.name || t('labels.unnamed')}
            </span>
          ))}
        </div>
        {snapshot.content_blocks.length === 0 && (
          <p className={styles.meta}>{t('history.noContent')}</p>
        )}
        {snapshot.content_blocks.map((block, index) =>
          block.checklist ? (
            <div key={index} className={styles.block}>
              <strong>{block.checklist.title || t('checklist.untitled')}</strong>
              <ul className={styles.items}>
                {block.checklist.items.map((item, itemIndex) => (
                  <li key={itemIndex} className={item.is_completed ? styles.done : undefined}>
                    {item.is_completed ? '☑' : '☐'} {item.text}
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <div key={index} className={styles.block}>
              <Suspense fallback={null}>
                <Markdown>{block.markdown_text ?? ''}</Markdown>
              </Suspense>
            </div>
          ),
        )}
      </div>
      <FormError error={restore.error} />
      <Button
        variant="primary"
        disabled={restore.isPending}
        onClick={() => {
          setConfirming(true);
        }}
      >
        {t('history.restore')}
      </Button>
      {confirming && (
        <ConfirmDialog
          title={t('history.restoreTitle', { number })}
          message={t('history.restoreMessage')}
          confirmLabel={t('history.restore')}
          confirmVariant="primary"
          pending={restore.isPending}
          onCancel={() => {
            setConfirming(false);
          }}
          onConfirm={() => {
            restore.mutate(
              { versionId, version: cardVersion },
              {
                onSuccess: () => {
                  setConfirming(false);
                  toast.show({ message: t('history.restored') });
                  onRestored();
                },
                onError: () => {
                  setConfirming(false);
                },
              },
            );
          }}
        />
      )}
    </section>
  );
}
